import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, TrendingUp, X } from "lucide-react";
import { searchCompanies } from "../../lib/api";
import { formatINR, formatPercent, signColor } from "../../lib/format";

const NAV = [
  { to: "/", label: "Markets", testId: "navbar-nav-markets", end: true },
  { to: "/discover", label: "Discover", testId: "navbar-nav-discover" },
  { to: "/companies", label: "Companies", testId: "navbar-nav-companies" },
  { to: "/portfolio", label: "Portfolio", testId: "navbar-nav-portfolio" },
  { to: "/learn", label: "Learn", testId: "navbar-nav-learn" },
];

const NAV_SPRING = { type: "spring", stiffness: 420, damping: 34, mass: 0.7 };

const Logo = () => (
  <Link to="/" data-testid="navbar-brand-logo" className="group flex items-center gap-2.5">
    <span className="relative flex h-7 w-7 items-center justify-center rounded-[7px] border border-white/15 bg-gradient-to-b from-[#1a1d23] to-[#0c0d10] transition-transform duration-200 group-hover:scale-105">
      <span className="absolute inset-x-1 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
      <TrendingUp className="h-4 w-4 text-amber-400" strokeWidth={2.25} aria-hidden="true" />
    </span>
    <div className="flex flex-col leading-none">
      <span className="font-mono text-[15px] font-bold tracking-[0.12em] text-white">LONGVIEW</span>
      <span className="mt-0.5 hidden font-mono text-[8px] tracking-[0.28em] text-zinc-500 sm:block">
        INDIA · NSE / BSE
      </span>
    </div>
  </Link>
);

// Desktop nav item: near-white active text, muted inactive text, and a thin
// 2px underline that glides between routes via a shared framer-motion layoutId.
const NavItem = ({ n }) => (
  <NavLink
    key={n.to}
    to={n.to}
    end={n.end}
    data-testid={n.testId}
    className="relative px-3 py-2 text-[13px] font-medium outline-none"
  >
    {({ isActive }) => (
      <>
        <span className={`relative z-10 transition-colors duration-200 ${isActive ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
          {n.label}
        </span>
        {isActive && (
          <motion.span
            layoutId="nav-underline"
            transition={NAV_SPRING}
            className="lv-nav-glow absolute inset-x-2 -bottom-[3px] h-[2px] rounded-full bg-amber-400/90"
          />
        )}
      </>
    )}
  </NavLink>
);

export const Navbar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searchFailed, setSearchFailed] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const data = await searchCompanies(q);
        if (active) {
          setResults(data.results || []);
          setSearchFailed(false);
        }
      } catch {
        if (active) {
          setResults([]);
          setSearchFailed(true);
        }
      }
    }, 140);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  const go = useCallback((ticker) => {
    setOpen(false); setQ(""); setResults([]);
    setSearchFailed(false);
    navigate(`/company/${ticker}`);
  }, [navigate]);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-white/[0.08] bg-[#070809]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Logo />
          <nav aria-label="Primary navigation" className="ml-6 hidden items-center gap-0.5 md:flex">
            {NAV.map((n) => <NavItem key={n.to} n={n} />)}
          </nav>
          <div className="ml-auto">
            <button
              type="button"
              aria-label="Search Indian companies"
              data-testid="global-search-trigger"
              onClick={() => setOpen(true)}
              className="lv-press group flex items-center gap-2 rounded-md border border-white/[0.09] bg-[#0B0C0E] px-3 py-1.5 text-zinc-400 transition-colors hover:border-white/20 hover:text-zinc-200"
            >
              <Search className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden text-[13px] sm:inline">Search Indian companies…</span>
              <span className="ml-2 hidden rounded border border-white/10 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500 sm:inline">⌘K</span>
            </button>
          </div>
        </div>
        {/* mobile nav */}
        <nav aria-label="Mobile navigation" className="flex items-center gap-1 overflow-x-auto border-t border-white/[0.06] px-4 py-2 md:hidden">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-200 ${
                  isActive ? "bg-white/[0.07] text-white" : "text-zinc-500"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true" aria-labelledby="company-search-title">
          <button type="button" tabIndex={-1} aria-label="Close company search" className="absolute inset-0 cursor-default bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="lv-fade-up relative w-full max-w-xl overflow-hidden rounded-xl border border-white/12 bg-[#101215] shadow-2xl">
            <h2 id="company-search-title" className="sr-only">Search Indian companies</h2>
            <div className="flex items-center gap-3 border-b border-white/10 px-4">
              <Search className="h-4 w-4 text-zinc-500" aria-hidden="true" />
              <input
                data-testid="global-search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by company name, ticker or sector…"
                aria-label="Search by company name, ticker, or sector"
                role="combobox"
                aria-expanded="true"
                aria-controls="global-search-options"
                aria-autocomplete="list"
                className="w-full bg-transparent py-4 text-sm text-white outline-none placeholder:text-zinc-500"
                onKeyDown={(e) => { if (e.key === "Enter" && results[0]) go(results[0].ticker); }}
              />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close company search" className="rounded p-1.5 text-zinc-500 transition-colors hover:text-zinc-200">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div id="global-search-options" role="listbox" data-testid="global-search-results" className="max-h-80 overflow-y-auto p-2" aria-live="polite">
              {searchFailed && (
                <p role="alert" className="px-3 py-6 text-center text-sm text-zinc-500">Company search is temporarily unavailable.</p>
              )}
              {!searchFailed && q.trim() && results.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-zinc-500">No companies match “{q}”.</p>
              )}
              {!searchFailed && !q.trim() && (
                <p className="px-3 py-6 text-center text-xs text-zinc-600">
                  Try “Reliance”, “TCS”, “bank” or “pharma”.
                </p>
              )}
              {results.map((r) => (
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  key={r.ticker}
                  data-testid={`search-result-${r.ticker.toLowerCase()}`}
                  onClick={() => go(r.ticker)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/[0.05]"
                >
                  <span className="flex h-8 w-14 items-center justify-center rounded border border-white/10 bg-[#0B0C0E] font-mono text-[11px] font-semibold text-zinc-300">
                    {r.ticker}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-white">{r.name}</span>
                    <span className="block truncate text-xs text-zinc-500">{r.sector}</span>
                  </span>
                  <span className="text-right">
                    <span className="block font-mono text-xs text-zinc-300">{formatINR(r.price)}</span>
                    <span className={`block font-mono text-[11px] ${signColor(r.change_percent)}`}>{formatPercent(r.change_percent)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
